import {
  faArrowDownArrowUp,
  faArrowLeft,
} from '@fortawesome/pro-regular-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { CircularProgress } from 'src/components/CircularProgress';
import { Container } from 'src/components/Container';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { ListItem } from 'src/components/ListItem';
import { Meta } from 'src/components/Meta';
import { SearchTextField } from 'src/components/SearchTextField';
import { Typography } from 'src/components/Typography';
import {
  fetchLeaderboards,
  fetchMeLeaderboardsOverview,
  fetchWorkoutPlansWithLeaderboards,
} from 'src/models/leaderboard/leaderboard';
import { AppFrameConfig } from 'src/shared/AppFrame/AppFrameConfig';
import { isUrlSafe } from 'src/utils/safeRedirect';

import { LeaderboardCard } from './components/LeaderboardCard';

type SortOption = {
  label: string;
  field: string;
  direction: string;
};

export const PublicLeaderboardsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('translation', {
    keyPrefix: 'leaderboard.public',
  });
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const sort = searchParams.get('sort') || 'name asc';
  const search = searchParams.get('search') || '';
  const [_sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);

  const updateParam = useCallback(
    (key: string, value: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value) {
          next.set(key, value);
        } else {
          next.delete(key);
        }
        return next;
      });
    },
    [setSearchParams]
  );

  const sortField = sort.split(' ')[0];
  const isPopularSort = sortField === 'results_count';

  const { data: leaderboardsData, isLoading: isLoadingLeaderboards } = useQuery(
    {
      queryKey: ['publicLeaderboards', isPopularSort ? sort : undefined],
      queryFn: () =>
        fetchLeaderboards({
          perPage: 100,
          visible: true,
          sortBy: isPopularSort ? 'results_count' : undefined,
          sortDir: isPopularSort ? 'desc' : undefined,
        }),
    }
  );

  const { data: workoutPlans, isLoading: isLoadingPlans } = useQuery({
    queryKey: ['leaderboardWorkoutPlans'],
    queryFn: fetchWorkoutPlansWithLeaderboards,
  });

  const { data: meOverview } = useQuery({
    queryKey: ['meLeaderboardsOverview'],
    queryFn: fetchMeLeaderboardsOverview,
  });

  const leaderboards = leaderboardsData?.leaderboard || [];
  const plans = workoutPlans || [];

  const allItems = [
    ...leaderboards.map((lb) => ({
      type: 'leaderboard' as const,
      item: lb,
    })),
    ...plans.map((plan: any) => ({
      type: 'workout' as const,
      item: plan,
    })),
  ];

  const filtered = search
    ? allItems.filter((entry) => {
        const name =
          entry.type === 'leaderboard' ? entry.item.name : entry.item.name;
        return name?.toLowerCase().includes(search.toLowerCase());
      })
    : allItems;

  const sorted = [...filtered].sort((a, b) => {
    if (isPopularSort) return 0;
    const [field, direction] = sort.split(' ');
    const aVal = a.type === 'leaderboard' ? a.item[field] : a.item[field];
    const bVal = b.type === 'leaderboard' ? b.item[field] : b.item[field];
    const aStr = typeof aVal === 'string' ? aVal.toLowerCase() : aVal;
    const bStr = typeof bVal === 'string' ? bVal.toLowerCase() : bVal;
    if (aStr < bStr) return direction === 'asc' ? -1 : 1;
    if (aStr > bStr) return direction === 'asc' ? 1 : -1;
    return 0;
  });

  const getUserResult = (leaderboardId: any) => {
    if (!meOverview) return null;
    return meOverview.find(
      (r) => r && r.leaderboardId?.toString() === leaderboardId?.toString()
    );
  };

  const sortOptions: SortOption[] = [
    { label: t('sort.mostRecent'), field: 'created_at', direction: 'desc' },
    { label: t('sort.aToZ'), field: 'name', direction: 'asc' },
    { label: t('sort.zToA'), field: 'name', direction: 'desc' },
    {
      label: t('sort.mostPopular'),
      field: 'results_count',
      direction: 'desc',
    },
  ];

  const handleSortClick = (option: SortOption) => {
    updateParam('sort', `${option.field} ${option.direction}`);
    setSortAnchorEl(null);
  };

  return (
    <>
      <Meta title={t('title')} />
      <AppFrameConfig
        id="public-leaderboards"
        containerMaxWidth="xl"
        title={t('title')}
        actions={
          <Button
            variant="bordered"
            startIcon={<Icon icon={faArrowLeft} />}
            component={Link}
            to="/leaderboards"
          >
            {t('backToAdmin')}
          </Button>
        }
      />
      <Container>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            mb: 3,
          }}
        >
          <SearchTextField
            placeholder={t('searchPlaceholder')}
            value={search}
            onSearch={(val) => updateParam('search', val)}
            onCancel={() => updateParam('search', '')}
            sx={{ width: '100%' }}
          />
          <DropdownMenu
            trigger={
              <IconButton size="small" onClick={() => setSortAnchorEl(null)}>
                <Icon icon={faArrowDownArrowUp} />
              </IconButton>
            }
          >
            {sortOptions.map((option) => (
              <ListItem
                primary={option.label}
                key={option.label}
                onClick={() => handleSortClick(option)}
              >
                {option.label}
              </ListItem>
            ))}
          </DropdownMenu>
        </Box>

        {isLoadingLeaderboards || isLoadingPlans ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
            {sorted.map((entry) => {
              if (entry.type === 'leaderboard') {
                const lb = entry.item;
                const actionLink =
                  lb.actionLink && isUrlSafe(lb.actionLink)
                    ? lb.actionLink
                    : undefined;
                return (
                  <LeaderboardCard
                    key={`lb-${lb.id}`}
                    name={lb.name}
                    image={lb.thumbnailUrl}
                    result={getUserResult(lb.id)}
                    onClick={() => navigate(`/leaderboards/public/${lb.id}`)}
                    actions={
                      actionLink ? (
                        <ListItem
                          key={`action-${lb.id}`}
                          primary={lb.actionLabel || t('action')}
                          onClick={() =>
                            window.open(
                              actionLink,
                              '_blank',
                              'noopener,noreferrer'
                            )
                          }
                          clickable
                        />
                      ) : undefined
                    }
                  />
                );
              }

              const plan = entry.item;
              return (
                <LeaderboardCard
                  key={`plan-${plan.id}`}
                  name={plan.name}
                  image={plan.image_url || plan.large_plan_image}
                  onClick={() =>
                    navigate(`/leaderboards/public/calendars/${plan.id}`)
                  }
                />
              );
            })}
            {sorted.length === 0 && (
              <Typography sx={{ m: 2 }}>{tCommon('noResults')}</Typography>
            )}
          </Box>
        )}
      </Container>
    </>
  );
};
